/**
 * 已知 EVM 链的 `wallet_addEthereumChain` 参数字典
 *
 * 后端 `BlockchainConfig` 当前只返回 `chainId` / `chainName` / `chainLogoUrl`，
 * 不返回 `rpcUrls` / `blockExplorerUrls` / `nativeCurrency`。
 * 当 `wallet_switchEthereumChain` 返回 4902（链未被添加）时，前端需要 fallback
 * 到 `wallet_addEthereumChain`，这里维护调用所需的参数。
 *
 * 新增链按下列步骤操作：
 * 1. 在 {@link KNOWN_EVM_CHAINS} 里按 `chainId` 补一条 `AddEthereumChainParameter`
 * 2. `chainName` / `blockExplorerUrls` / `iconUrls` 会被 `BlockchainConfig` 的实际返回值覆盖
 * 3. 在 [docs/ABI模块文档.md](../../docs/ABI模块文档.md) 的"新增链"章节补条记录
 */

import { toBeHex } from 'ethers';
import { WalletError, getEip1193ErrorCode } from './walletError';

/**
 * EIP-3085 `wallet_addEthereumChain` 参数
 * 参见 https://eips.ethereum.org/EIPS/eip-3085
 */
export interface AddEthereumChainParameter {
  chainId: string; // 0x 前缀的十六进制字符串
  chainName: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  rpcUrls: string[];
  blockExplorerUrls?: string[];
  iconUrls?: string[];
}

/**
 * chainId（十进制）→ 链参数
 * 目前仅支持 BSC 主网 / 测试网，其它链用到时请补充。
 */
export const KNOWN_EVM_CHAINS: Record<number, AddEthereumChainParameter> = {
  56: {
    chainId: '0x38',
    chainName: 'BNB Smart Chain Mainnet',
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
    rpcUrls: [
      'https://bsc-dataseed.bnbchain.org',
      'https://bsc-dataseed1.binance.org',
    ],
    blockExplorerUrls: ['https://bscscan.com'],
  },
  97: {
    chainId: '0x61',
    chainName: 'BNB Smart Chain Testnet',
    nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
    rpcUrls: [
      'https://data-seed-prebsc-1-s1.bnbchain.org:8545',
      'https://data-seed-prebsc-1-s1.binance.org:8545',
    ],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
  },
};

/** EIP-3085：目标链未被钱包添加 */
const CHAIN_NOT_ADDED_CODE = 4902;

/** EIP-1193 provider 最小接口 */
interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

/** 覆盖后端返回值用于 `wallet_addEthereumChain` 的可选字段 */
export interface AddChainOverrides {
  chainName?: string;
  iconUrl?: string;
  blockExplorerUrl?: string;
}

/**
 * 切换钱包到指定 EVM 链
 *
 * 行为：
 * 1. 读取当前 `eth_chainId`，若与目标一致则 no-op
 * 2. 调用 `wallet_switchEthereumChain`
 * 3. 若返回 4902（链未被钱包添加），读取 {@link KNOWN_EVM_CHAINS} 调用
 *    `wallet_addEthereumChain` 添加链后自动切换
 * 4. 若 chainId 不在字典里，抛错提示需要手动添加
 *
 * @param provider EIP-1193 provider（由 walletkit 提供）
 * @param chainId  目标链十进制 chainId（例如 BSC 主网 `56`、测试网 `97`）
 * @param overrides 用后端返回值覆盖 `chainName` / `iconUrl` / `blockExplorerUrl`
 */
export async function switchOrAddChain(
  provider: EIP1193Provider,
  chainId: number,
  overrides: AddChainOverrides = {}
): Promise<void> {
  const targetHex = toBeHex(chainId);
  const currentHex = (await provider.request({ method: 'eth_chainId' })) as string;

  if (currentHex?.toLowerCase() === targetHex.toLowerCase()) return;

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: targetHex }],
    });
    return;
  } catch (error) {
    if (getEip1193ErrorCode(error) !== CHAIN_NOT_ADDED_CODE) throw error;
  }

  const base = KNOWN_EVM_CHAINS[chainId];
  if (!base) {
    // Classified so callers show a friendly i18n message instead of leaking this
    // English developer note to end users.
    throw new WalletError(
      'chainNotConfigured',
      `Chain ${chainId} is not configured in KNOWN_EVM_CHAINS. ` +
        'Please add its RPC / explorer parameters in src/utils/evmChains.ts.'
    );
  }

  const params: AddEthereumChainParameter = {
    ...base,
    chainName: overrides.chainName || base.chainName,
    blockExplorerUrls: overrides.blockExplorerUrl
      ? [overrides.blockExplorerUrl]
      : base.blockExplorerUrls,
    iconUrls: overrides.iconUrl ? [overrides.iconUrl] : base.iconUrls,
  };

  await provider.request({
    method: 'wallet_addEthereumChain',
    params: [params],
  });
}
